import { parse } from 'papaparse';

import { isValidDecimal } from '../../../utils/math/number';
import { getGeoidLongitudeRange } from '../../../utils/proj4/geoid';
import { isDefined } from '../../../utils/variables';
import { CoordinateManager } from '../../../viewer/coordinate-manager';
import { projectReferenceAdapter } from '../../state/project-reference-state/project-reference-adapter';

export type NameFieldStatus = 'duplication' | 'valid' | 'invalid';
export type NEFieldStatus = 'invalid_format' | 'invalid_geoid' | 'valid_format' | 'valid_geoid';
export type HeightFieldStatus = 'invalid_format' | 'valid_format';

type ValueField<T> = {
  status: T;
  value: string;
};

type NameField = ValueField<NameFieldStatus>;
type NEField = ValueField<NEFieldStatus>;
type HeightField = ValueField<HeightFieldStatus>;

export type GcpRow = {
  e: NEField;
  h: HeightField;
  included: boolean;
  n: NEField;
  name: NameField;
};

/**
 * Parses the specified string (CSV) and returns a list of rows with status indicating whether each
 * field is valid or not, and if coordinate (n,e) is valid against the project geoid (if any). If no
 * coordinates are valid it flips coordinate to (e,n) and retries the validation against the project
 * geoid.
 */
export const parseGcpFileContent = (data: string): GcpRow[] => {
  let rows = csvToRows(data);

  // Try validate coordinates (n,e) in the order they are set in the CSV file.
  rows = validateCoordinatesAgainstProjectGeoid(rows, (n, e) => ({ n, e }));

  // If no coordinates are valid we can try to validate all rows against project geoid again, but
  // with switched northing and easting values. If there is at least one valid coordinate we can
  // assume that this set of rows are the correct one, and use these rows instead. If no valid
  // coordinates exist we can keep using the first set of rows.
  if (!hasAtLeastOneValidCoordinate(rows)) {
    const rowsSwitchedNE = validateCoordinatesAgainstProjectGeoid(rows, (n, e) => ({ n: e, e: n }));

    // Use this set of rows if at least one valid coordinate exists.
    if (hasAtLeastOneValidCoordinate(rowsSwitchedNE)) {
      rows = rowsSwitchedNE;

      // Also, switch all other fields where coordinate is not valid (to have all rows switched).
      rows
        .filter((x) => x.n.status === 'invalid_format' || x.e.status === 'invalid_format')
        .forEach((x) => {
          [x.n, x.e] = [x.e, x.n];
        });
    }
  }

  // Remove header if any.
  if (hasHeader(rows)) {
    rows = rows.slice(1);
  }

  checkForNameDuplications(rows);

  return rows;
};

/**
 * Returns a value indicating whether the specified row has at least one invalid field.
 */
export const isInvalidGcpRow = (row: GcpRow) =>
  row.name.status === 'duplication' ||
  row.name.status === 'invalid' ||
  row.n.status === 'invalid_format' ||
  row.n.status === 'invalid_geoid' ||
  row.e.status === 'invalid_format' ||
  row.e.status === 'invalid_geoid' ||
  row.h.status === 'invalid_format';

/**
 * Returns a value indicating whether the specified field is considered valid (format, geoid).
 */
export const isValidCoordinateField = (field: NEField) =>
  field.status === 'valid_format' || field.status === 'valid_geoid';

/**
 * Returns a value indicating whether the specified coordinate (n,e) is considered valid.
 */
const isValidCoordinate = (n: NEField, e: NEField) => {
  return isValidCoordinateField(n) && isValidCoordinateField(e);
};

/**
 * Returns a value indicating whether at least one coordinate is considered valid.
 */
const hasAtLeastOneValidCoordinate = (rows: GcpRow[]) =>
  rows.some((row) => isValidCoordinate(row.n, row.e));

/**
 * Checks whether coordinates in specified rows is within projects geoid.
 * @param buildCoordinate Function that builds a coordinate given N and E.
 */
const validateCoordinatesAgainstProjectGeoid = (
  rows: GcpRow[],
  buildCoordinate: (n: string, e: string) => { n: string; e: string },
) => {
  return rows.map((row) => {
    // If this function is run twice, allow it to run when status is either valid_format or
    // invalid_geoid to check it again using flipped coordinate (e,n).
    const checkCoordinate =
      (row.n.status === 'valid_format' || row.n.status === 'invalid_geoid') &&
      (row.e.status === 'valid_format' || row.e.status === 'invalid_geoid');

    if (checkCoordinate) {
      const coord = buildCoordinate(row.n.value, row.e.value);
      const status = validateAgainstProjectGeoid(coord.n, coord.e);

      return {
        ...row,
        n: {
          value: coord.n,
          status,
        },
        e: {
          value: coord.e,
          status,
        },
      };
    }

    return row;
  });
};

/**
 * Check if first row is an expected header. Header expected if N, E and H are all not floats.
 */
const hasHeader = (rows: GcpRow[]) => {
  const isHeaderValue = (field: NEField) => field.value !== '' && !isValidDecimal(field.value);

  return rows.length > 0
    ? rows[0].name.value !== '' &&
        isHeaderValue(rows[0].n) &&
        isHeaderValue(rows[0].e) &&
        isHeaderValue(rows[0].h)
    : false;
};

/**
 * Checks whether the specified coordinate (n,e) is within the projects geoid, if any. If check fails,
 * format of either N or E is considered invalid. If no geoid exists "valid_format" is returned.
 */
const validateAgainstProjectGeoid = (n: string, e: string): NEFieldStatus => {
  const snap = projectReferenceAdapter.getSnapshot();
  const header = snap.geoid?.header;
  const proj4 = snap.coordinateSystem?.proj4!;

  if (header) {
    try {
      const coordinates = CoordinateManager.getInstance().convertCoords(
        parseFloat(e),
        parseFloat(n),
        proj4,
        'wgs84',
      );

      const lng = getGeoidLongitudeRange(header, coordinates.x);
      const latOutOfBounds = coordinates.y < header.lat.min || coordinates.y > header.lat.max;
      const lngOutOfBounds = coordinates.x < lng.min || coordinates.x > lng.max;
      return latOutOfBounds || lngOutOfBounds ? 'invalid_geoid' : 'valid_geoid';
    } catch (err) {
      // Treat any errors from proj4 as invalid format.
      return 'invalid_format';
    }
  }

  // If no geoid exists, value is considered valid.
  return 'valid_format';
};

/**
 * Expects input string as CSV formatted and parses the first four columns to Name, N, E and H.
 * Also sets status for each value indicating whether the value is valid or not.
 */
const csvToRows = (data: string): GcpRow[] => {
  const result = parse<string[]>(data, { skipEmptyLines: true });

  const buildCoordinateField = <T>(value?: string): T =>
    ({
      status: isDefined(value) && isValidDecimal(value.trim()) ? 'valid_format' : 'invalid_format',
      value: value?.trim() ?? '',
    }) as T;

  return result.data.map((x) => {
    const nameField: NameField = {
      status: x[0].trim() === '' ? 'invalid' : 'valid',
      value: x[0].trim(),
    };

    return {
      n: buildCoordinateField(x[1]),
      e: buildCoordinateField(x[2]),
      h: buildCoordinateField(x[3]),
      name: nameField,
      included: true,
    };
  });
};

/**
 * Iterates all rows and sets status to `duplication` on all rows where name is identical (case
 * insensitive).
 */
const checkForNameDuplications = (rows: GcpRow[]) => {
  const names = new Map<string, number>();
  const getName = (row: GcpRow) => row.name.value.trim().toLowerCase();

  // Count each name.
  rows
    .filter((x) => x.name.value !== '')
    .forEach((x) => {
      if (!names.has(getName(x))) {
        names.set(getName(x), 0);
      }

      names.set(getName(x), names.get(getName(x))! + 1);
    });

  // Check for duplications.
  rows.forEach((x) => {
    if (names.get(getName(x))! > 1) {
      x.name.status = 'duplication';
    }
  });
};
